import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { WizardRail } from '@/components/wizard/WizardRail';
import type { RailStep } from '@/components/wizard/railStep';
import { useOrganization } from '@/api/queries/useOrganization';
import { WIZARD_STEPS, type WizardProgress, type WizardStepId } from '../wizardSteps';

interface SetupWizardRailProps {
  currentStep: WizardStepId;
  progress: WizardProgress;
  skippedSteps: Set<WizardStepId>;
  onStepClick: (stepId: WizardStepId) => void;
}

/**
 * The setup wizard's spine: numbered steps 1-6, the Manager-auflösen sub-step
 * under Mitarbeiter, and the flag entry for Überblick & Abschluss at the
 * bottom. Welcome/outcomes are intro screens and have no entry.
 */
export function SetupWizardRail({
  currentStep,
  progress,
  skippedSteps,
  onStepClick
}: SetupWizardRailProps) {
  const { t } = useTranslation();
  const { data: org } = useOrganization();

  const steps = useMemo<RailStep<WizardStepId, WizardProgress>[]>(
    () =>
      WIZARD_STEPS.filter((step) => !step.isIntro).map((step) => ({
        id: step.id,
        railNumber: step.railNumber,
        subStepOf: step.subStepOf,
        label: step.labelKey ? t(step.labelKey) : '',
        description: step.descriptionKey ? t(step.descriptionKey) : undefined,
        isDone: step.isDone,
        isReachable: step.isReachable,
        isVisible: step.isVisible
      })),
    [t]
  );

  return (
    <WizardRail
      steps={steps}
      currentStep={currentStep}
      state={progress}
      skippedSteps={skippedSteps}
      onStepClick={onStepClick}
      header={
        <div className="flex items-center gap-3">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-bliks-green-500/25 bg-bliks-green-500/10">
            <img
              src="/assets/images/logo.svg"
              className="h-[25px] w-[25px]"
              alt={t('initialSetup.topBar.logoAlt')}
            />
          </span>
          <div>
            <div className="font-display text-[17px] font-bold text-foreground">
              {t('initialSetup.title')}
            </div>
            <div className="text-xs text-fg-secondary">{org?.name ?? ''}</div>
          </div>
        </div>
      }
    />
  );
}
